/**
 * xvi Label component
 */
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FormControl } from '@angular/forms';
import {
  Observable,
  observableCombineLatest,
  ReplaySubject,
  rx,
} from '@shared/rx';
import { onControlValueChanged } from '@shared/rx-operators';
import { asObservable, isObservable } from '@shared/utils';
import { IWidgetData } from '../services';
import { get } from 'lodash';
import { TooltipIconConfig } from '@app/shared/types';

@Component({
  selector: 'xvi-label',
  templateUrl: './label.component.html',
  styleUrls: ['./label.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XVILabelComponent implements OnInit {
  @Input() public set titleObs(obs: Observable<string>) {
    this._titleObs$.next(obs);
  }
  @Input() public set control(control: FormControl) {
    this._control$.next(control);
  }
  @Input() public set title(value: string | Observable<string>) {
    this._title$.next(value);
  }
  @Input() public itemClass: string;
  @Input() public widgetData: IWidgetData = {};
  public text$: Observable<string>;
  public html$: Observable<string>;
  public otherTips: string;
  public clickFn: Function;
  public iconConfig: TooltipIconConfig;
  private _titleObs$: ReplaySubject<Observable<string>> = new ReplaySubject(1);
  private _title$: ReplaySubject<Observable<string> | string> =
    new ReplaySubject(1);
  private _control$: ReplaySubject<FormControl> = new ReplaySubject(1);

  public constructor(private readonly sanitizer: DomSanitizer) {}

  public ngOnInit(): void {
    this.initStreams();
    this.clickFn = get(this, 'widgetData.clickFn', null);
    this.iconConfig = get(this, 'widgetData.iconConfig', null);
    this.otherTips = get(this, 'widgetData.otherTips', null);
  }

  public onClickLabel(): void {
    if (this.clickFn) {
      this.clickFn();
    }
  }

  private initStreams(): void {
    this.text$ = observableCombineLatest([
      this._titleObs$.pipe(
        rx.filter((obs) => isObservable(obs)),
        rx.switchAll(),
        // eslint-disable-next-line import/no-deprecated
        rx.startWith(undefined),
      ),
      this._title$.pipe(
        rx.switchMap((value) => asObservable(value)),
        // eslint-disable-next-line import/no-deprecated
        rx.startWith(undefined),
      ),
      // eslint-disable-next-line import/no-deprecated
      this._control$.pipe(onControlValueChanged(), rx.startWith(undefined)),
    ]).pipe(
      rx.map(
        ([fromTitleObs, fromTitle, formControl]) =>
          fromTitleObs ?? formControl ?? fromTitle,
      ),
    );

    this.html$ = this.text$.pipe(
      rx.map((htmlContent) => this.sanitizer.sanitize(1, htmlContent)),
    );
  }
}
